import { read_cookie } from 'sfcookies';
import { hostUrl, UseUrlManagerCleanUrl } from '../utils/url-manager';

/**
 * The HQ change log: what has shipped to production, as HQ-API serves it from its `CHANGELOG.md`.
 *
 * Kept outside react-admin's data provider on purpose. It is one read-only list with no paging,
 * sorting or filtering, and a failure here should say so rather than render as "nothing found"
 * the way most data providers do (HQ-06).
 */

/** In the order a release lists them. */
export const CHANGE_TYPES = ['Added', 'Changed', 'Fixed', 'Removed'] as const;

export type ChangeType = typeof CHANGE_TYPES[number];

export type ChangelogEntry = {
    type: ChangeType,
    /** HQ, HQ-API, NN, Operator or Daive. */
    app: string,
    /** One sentence of inline markdown. */
    text: string,
    /** Tracker ids such as HQAPI-49, possibly none. */
    refs: string[],
};

export type ChangelogRelease = {
    /** `YYYY.MM.DD`, or `YYYY.MM.DD.N` for a later release the same day. */
    version: string,
    /** `YYYY-MM-DD`. */
    date: string,
    entries: ChangelogEntry[],
};

export type EntryGroup = {
    type: ChangeType,
    entries: ChangelogEntry[],
};

/** A release's entries under their headings, in the fixed heading order, empty headings left out. */
export const groupByType = (entries: ChangelogEntry[]): EntryGroup[] =>
    CHANGE_TYPES
        .map((type: ChangeType): EntryGroup => ({
            type,
            entries: entries.filter((entry: ChangelogEntry): boolean => entry.type === type),
        }))
        .filter((group: EntryGroup): boolean => group.entries.length > 0);

/**
 * Formatted in UTC because the date has no time. Read as local midnight and formatted in a zone
 * behind UTC, `2026-09-14` would print as the 13th.
 */
const releaseDateFormat: Intl.DateTimeFormat = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
});

/** `2026-09-14` -> `14 September 2026`. Anything unreadable is shown as it came. */
export const formatReleaseDate = (date: string): string => {
    const parsed: Date = new Date(`${ date }T00:00:00Z`);

    return Number.isNaN(parsed.getTime()) ? date : releaseDateFormat.format(parsed);
};

/** Newest release first. Throws on any failure, so the caller can show one. */
export const loadReleases = async (): Promise<ChangelogRelease[]> => {
    const token: string = read_cookie('auth');
    if (typeof token !== 'string' || token.length === 0) {
        throw new Error('Not signed in');
    }

    const url: string = UseUrlManagerCleanUrl({ url: `${ hostUrl }hq/changelog` });
    const response: Response = await fetch(new Request(url, {
        method: 'GET',
        headers: new Headers({ 'Content-Type': 'application/json', 'token': token, 'hq': 'hq' }),
    }));
    if (!response.ok) {
        throw new Error('Could not load the change log');
    }

    const body: any = await response.json();
    if (body?.status !== 'success' || !Array.isArray(body?.releases)) {
        throw new Error('The change log came back in an unexpected shape');
    }

    return body.releases;
};
